import type { SubscribePayload } from 'graphql-ws';
import {
	type CacheConfig,
	Observable,
	type RequestParameters,
	type SubscribeFunction,
	type Variables,
} from 'relay-runtime';

import { fg } from '@atlaskit/platform-feature-flags';
import { getClient, getSinkError, type SinkError } from '@atlassian/graphql-subscription';

export type SubscribeOptions = {
	subscribeExtensions?: Record<string, string>;
	onSubscribeTrigger?: (payload: SubscribePayload) => void;
	onSubscribeError?: (payload: SubscribePayload & { requestId: string | null }) => void;
};

let cachedDefaultSubscribe: SubscribeFunction | undefined;

export function createSubscribeFn(subscribeOptions?: SubscribeOptions): SubscribeFunction;
export function createSubscribeFn(subscribeOptions?: SubscribeOptions): SubscribeFunction {
	if (typeof subscribeOptions === 'undefined' && typeof cachedDefaultSubscribe !== 'undefined') {
		return cachedDefaultSubscribe;
	}

	const fn: SubscribeFunction = (
		{ id, name: operationName }: RequestParameters,
		variables: Variables,
		cacheConfig: CacheConfig,
	) =>
		Observable.create((sink) => {
			const client = getClient();
			if (!client) {
				return sink.error(new Error('Unable to create subscription client'));
			}
			const replayConfig = cacheConfig?.metadata?.replay || undefined;

			// When the fix gate is ON and the caller supplies a `command` key inside
			// replayConfig, hoist it to extensions.command (flat) to match AGG's
			// expected wire shape. AGG reads extensions["command"] directly; the
			// previous extensions.replay.command nesting was silently dropped and no
			// Replay ChannelCommand was ever issued. See CRCS-3291.
			// When the gate is OFF the old extensions.replay: {...} shape is preserved,
			// which is the broken-but-stable status-quo for consumers not using replay.
			const replayCommand =
				replayConfig !== null && typeof replayConfig === 'object' && 'command' in replayConfig
					? (replayConfig as Record<string, unknown>).command
					: undefined;
			const replayExtensions =
				replayCommand && fg('platform_relay_subscription_fix_replay_command')
					? { command: replayCommand }
					: replayConfig
						? { replay: replayConfig }
						: {};

			const payload: SubscribePayload = {
				query: '',
				operationName,
				variables,
				extensions: {
					pq: id,
					...(subscribeOptions?.subscribeExtensions || {}),
					...replayExtensions,
				},
			};

			let requestId: string | null = null;

			subscribeOptions?.onSubscribeTrigger?.(payload);

			return client.subscribe(payload, {
				...sink,
				next: (value) => {
					// @ts-expect-error agg prop is missing on generic "extensions" type
					const aggRequestId = value?.extensions?.gateway?.request_id;
					requestId = aggRequestId ?? requestId;
					// @ts-expect-error incompatible types between two libraries
					sink.next(value);
				},
				error: (sinkError: SinkError) => {
					const errorInfo = getSinkError(sinkError);
					sink.error(errorInfo);
					subscribeOptions?.onSubscribeError?.({ ...payload, requestId });
				},
			});
		});

	if (typeof subscribeOptions === 'undefined') {
		cachedDefaultSubscribe = fn;
	}

	return fn;
}

export const subscribe: SubscribeFunction = createSubscribeFn();
